/**
 * @fileoverview Public surface of @puffin-core/status-taxonomy (CJS).
 *
 * Single source of truth (EAS-001) for the two employee status axes —
 * ACCOUNT (login/app-access) and EMPLOYMENT (HR) — plus display labels,
 * badge tones (the closed `.badge[data-tone]` vocabulary), and the derived helpers used by the roster reader,
 * scheduling gates, and onboarding UI.
 *
 * Consumed by:
 *   - `@puffin-core/auth-routes` login/refresh/MFA gates (account-active)
 *   - `@puffin-core/invite-core` invite state machine
 *   - `@puffin-core/unified-models` UserModel + UserLocationAssignmentModel
 *   - every tenant API's models / passkey / scheduling services
 *   - pos-web + planner-web team surfaces (badges, chips, edit drawers)
 *
 * Drift is structurally caught by
 * `tests/cross-app/contracts/userStatusTaxonomy.contract.test.js` +
 * the `api/no-hardcoded-account-status-literal` / `web/no-local-status-label-map`
 * ESLint rules.
 *
 * @module @puffin-core/status-taxonomy
 */

'use strict';

const constants = require('./constants');
const badgeTones = require('./badgeTones');
const helpers = require('./helpers');
const dayPortionOverlap = require('./dayPortionOverlap');
const dayPortionWindow = require('./dayPortionWindow');
const leaveBlackouts = require('./leaveBlackouts');
const shipmentStatus = require('./shipmentStatus');
const mailItemStatus = require('./mailItemStatus');
const mailboxRental = require('./mailboxRental');
const mailboxRentPayment = require('./mailboxRentPayment');
const mailboxAgreement = require('./mailboxAgreement');
const customerStatus = require('./customerStatus');
const printJobStatus = require('./printJobStatus');
const operationRunStatus = require('./operationRunStatus');
const orderSource = require('./orderSource');
const registerTransaction = require('./registerTransaction');
const registerPayment = require('./registerPayment');
const orderLineType = require('./orderLineType');
const orderStatus = require('./orderStatus');
const ttsProvider = require('./ttsProvider');
const printQueueJobStatus = require('./printQueueJobStatus');
const inventoryMovementType = require('./inventoryMovementType');

module.exports = {
  // ── Enums ──
  ACCOUNT_STATUS: constants.ACCOUNT_STATUS,
  EMPLOYMENT_STATUS: constants.EMPLOYMENT_STATUS,
  ACCOUNT_STATUS_VALUES: constants.ACCOUNT_STATUS_VALUES,
  EMPLOYMENT_STATUS_VALUES: constants.EMPLOYMENT_STATUS_VALUES,
  DEFAULT_ACCOUNT_STATUS: constants.DEFAULT_ACCOUNT_STATUS,
  DEFAULT_EMPLOYMENT_STATUS: constants.DEFAULT_EMPLOYMENT_STATUS,
  SCHEDULABLE_PREDICATE: constants.SCHEDULABLE_PREDICATE,

  // ── Employment terms (users.employmentType / payType / terminationReason; Q-R8CLOSE-5) ──
  EMPLOYMENT_TYPE: constants.EMPLOYMENT_TYPE,
  EMPLOYMENT_TYPE_VALUES: constants.EMPLOYMENT_TYPE_VALUES,
  DEFAULT_EMPLOYMENT_TYPE: constants.DEFAULT_EMPLOYMENT_TYPE,
  PAY_TYPE: constants.PAY_TYPE,
  PAY_TYPE_VALUES: constants.PAY_TYPE_VALUES,
  TERMINATION_REASON: constants.TERMINATION_REASON,
  TERMINATION_REASON_VALUES: constants.TERMINATION_REASON_VALUES,

  // ── Undoing a created account (owner ruling Q-USERDEL-1) ──
  UNACCEPTED_ACCOUNT_STATUSES: constants.UNACCEPTED_ACCOUNT_STATUSES,
  ACCOUNT_UNDO_OUTCOME: constants.ACCOUNT_UNDO_OUTCOME,
  ACCOUNT_UNDO_OUTCOME_VALUES: constants.ACCOUNT_UNDO_OUTCOME_VALUES,

  // ── Refusing a deactivation (Q-USERDEL-2 parity with the retired DELETE) ──
  ACCOUNT_DEACTIVATION_REFUSAL: constants.ACCOUNT_DEACTIVATION_REFUSAL,
  ACCOUNT_DEACTIVATION_REFUSAL_VALUES: constants.ACCOUNT_DEACTIVATION_REFUSAL_VALUES,
  ACCOUNT_DEACTIVATION_SYSTEM_ACTOR: constants.ACCOUNT_DEACTIVATION_SYSTEM_ACTOR,
  ACCOUNT_DEACTIVATION_SYSTEM_ACTOR_VALUES: constants.ACCOUNT_DEACTIVATION_SYSTEM_ACTOR_VALUES,
  // ── Refusing a reactivation / a last-Owner role change (owner rulings 2026-10-07) ──
  ACCOUNT_REACTIVATION_REFUSAL: constants.ACCOUNT_REACTIVATION_REFUSAL,
  ACCOUNT_REACTIVATION_REFUSAL_VALUES: constants.ACCOUNT_REACTIVATION_REFUSAL_VALUES,
  ACCOUNT_ACTIVATION_SYSTEM_ACTOR: constants.ACCOUNT_ACTIVATION_SYSTEM_ACTOR,
  ACCOUNT_ACTIVATION_SYSTEM_ACTOR_VALUES: constants.ACCOUNT_ACTIVATION_SYSTEM_ACTOR_VALUES,
  // ── Refusing an administrator's password reset (OWNER-RULINGS-WALK-BUILDS N61) ──
  ACCOUNT_PASSWORD_RESET_REFUSAL: constants.ACCOUNT_PASSWORD_RESET_REFUSAL,
  ACCOUNT_PASSWORD_RESET_REFUSAL_VALUES: constants.ACCOUNT_PASSWORD_RESET_REFUSAL_VALUES,
  // ── Refusing an administrator's authentication reset (OWNER-RULINGS-WALK-BUILDS N71) ──
  ACCOUNT_AUTHENTICATION_RESET_REFUSAL: constants.ACCOUNT_AUTHENTICATION_RESET_REFUSAL,
  ACCOUNT_AUTHENTICATION_RESET_REFUSAL_VALUES:
    constants.ACCOUNT_AUTHENTICATION_RESET_REFUSAL_VALUES,
  // ── Refusing a manager's reset of a team member's calendar link (VALIDATION-ECHO-001 P4) ──
  CALENDAR_SYNC_REVOKE_REFUSAL: constants.CALENDAR_SYNC_REVOKE_REFUSAL,
  CALENDAR_SYNC_REVOKE_REFUSAL_VALUES: constants.CALENDAR_SYNC_REVOKE_REFUSAL_VALUES,
  // ── Refusing a platform owner restore (owner ruling 2026-10-07, "One platform staff") ──
  PLATFORM_OWNER_RESTORE_REFUSAL: constants.PLATFORM_OWNER_RESTORE_REFUSAL,
  PLATFORM_OWNER_RESTORE_REFUSAL_VALUES: constants.PLATFORM_OWNER_RESTORE_REFUSAL_VALUES,
  RESTORABLE_OWNER_ACCOUNT_STATUSES: constants.RESTORABLE_OWNER_ACCOUNT_STATUSES,
  ROLE_CHANGE_REFUSAL: constants.ROLE_CHANGE_REFUSAL,
  ROLE_CHANGE_REFUSAL_VALUES: constants.ROLE_CHANGE_REFUSAL_VALUES,

  // ── Committed shift-row status (employee_schedules; PLN-SCHED-LIFECYCLE-001) ──
  EMPLOYEE_SCHEDULE_STATUS: constants.EMPLOYEE_SCHEDULE_STATUS,
  ACTIVE_EMPLOYEE_SCHEDULE_STATUSES: constants.ACTIVE_EMPLOYEE_SCHEDULE_STATUSES,
  EMPLOYEE_SCHEDULE_STATUS_VALUES: constants.EMPLOYEE_SCHEDULE_STATUS_VALUES,

  // ── B2B partner status (purchase `partners.status`; UMB-001 lane SEC) ──
  PARTNER_STATUS: constants.PARTNER_STATUS,
  PARTNER_STATUS_VALUES: constants.PARTNER_STATUS_VALUES,

  // ── Payroll-run status (payday_payroll_runs; HTP-001) ──
  PAYROLL_RUN_STATUS: constants.PAYROLL_RUN_STATUS,
  PAYROLL_RUN_STATUS_TRANSITIONS: constants.PAYROLL_RUN_STATUS_TRANSITIONS,
  PAYROLL_RUN_STATUS_VALUES: constants.PAYROLL_RUN_STATUS_VALUES,
  PAYROLL_RUN_REVIEW_ACKNOWLEDGEABLE_STATUSES:
    constants.PAYROLL_RUN_REVIEW_ACKNOWLEDGEABLE_STATUSES,
  PAYROLL_RUN_SKIPPABLE_STATUSES: constants.PAYROLL_RUN_SKIPPABLE_STATUSES,
  PAYROLL_RUN_TYPE: constants.PAYROLL_RUN_TYPE,
  PAYROLL_RUN_TYPE_VALUES: constants.PAYROLL_RUN_TYPE_VALUES,
  PAYROLL_RUN_CANCELLABLE_TYPES: constants.PAYROLL_RUN_CANCELLABLE_TYPES,
  PAYROLL_RUN_WITHHOLDING_SWITCHABLE_STATUSES:
    constants.PAYROLL_RUN_WITHHOLDING_SWITCHABLE_STATUSES,

  // ── Payroll funding status (a run's funding pre-check; PAYDAY-RUN-LIFECYCLE-001) ──
  PAYROLL_FUNDING_STATUS: constants.PAYROLL_FUNDING_STATUS,
  PAYROLL_FUNDING_STATUS_VALUES: constants.PAYROLL_FUNDING_STATUS_VALUES,
  PAYROLL_FUNDING_STATUS_BADGE_TONES: constants.PAYROLL_FUNDING_STATUS_BADGE_TONES,
  PAYROLL_FUNDING_NOT_CHECKED_REASON: constants.PAYROLL_FUNDING_NOT_CHECKED_REASON,
  PAYROLL_FUNDING_NOT_CHECKED_REASON_VALUES: constants.PAYROLL_FUNDING_NOT_CHECKED_REASON_VALUES,

  // ── Payroll onboarding stage (payday_employee_payroll_mappings; PAYDAY-RUN-LIFECYCLE-001) ──
  PAYROLL_ONBOARDING_STATUS: constants.PAYROLL_ONBOARDING_STATUS,
  PAYROLL_ONBOARDING_STATUS_VALUES: constants.PAYROLL_ONBOARDING_STATUS_VALUES,
  PAYROLL_ONBOARDING_STATUS_BADGE_TONES: constants.PAYROLL_ONBOARDING_STATUS_BADGE_TONES,
  PAYROLL_ONBOARDING_STATUS_FRESH_FOR_MS: constants.PAYROLL_ONBOARDING_STATUS_FRESH_FOR_MS,
  isOnboardingCompleted: constants.isOnboardingCompleted,

  // ── Excluded-day source (planner_excluded_days; PLN-SCHED-LIFECYCLE-001 / LEAVE-CANON-001 P3c) ──
  EXCLUDED_DAY_SOURCE: constants.EXCLUDED_DAY_SOURCE,
  EXCLUDED_DAY_SOURCE_VALUES: constants.EXCLUDED_DAY_SOURCE_VALUES,
  // ── History-exclusion resolver vocabulary (the register + the derived holiday; PLN-DEPT-TAB-004) ──
  HISTORY_EXCLUSION_SOURCE: constants.HISTORY_EXCLUSION_SOURCE,
  HISTORY_EXCLUSION_SOURCE_PRECEDENCE: constants.HISTORY_EXCLUSION_SOURCE_PRECEDENCE,
  HISTORY_EXCLUSION_USER_SOURCES: constants.HISTORY_EXCLUSION_USER_SOURCES,
  HISTORY_EXCLUSION_COUNTED_SOURCES: constants.HISTORY_EXCLUSION_COUNTED_SOURCES,
  // PLN-LY-TWO-GRAINS-001 (R1) — the ONE reading of a resolved row's `sources[]`, both tiers.
  isUserHistoryExclusion: constants.isUserHistoryExclusion,
  // ── Historical-read basis (baseline = the register applied, actuals = the tills; PLN-DEPT-TAB-004 R3) ──
  HISTORICAL_BASIS: constants.HISTORICAL_BASIS,
  HISTORICAL_BASIS_VALUES: constants.HISTORICAL_BASIS_VALUES,
  // ── DOW-weight basis (the weights' stored `provenance.basis`; PLN-LY-TWO-GRAINS-001 R10) ──
  DOW_WEIGHT_BASIS: constants.DOW_WEIGHT_BASIS,
  DOW_WEIGHT_BASIS_VALUES: constants.DOW_WEIGHT_BASIS_VALUES,
  // PLN-LY-TWO-GRAINS-001 U3: the sales-basis marker every grain-aware figure carries.
  SALES_BASIS_GRAIN: constants.SALES_BASIS_GRAIN,
  SALES_BASIS_GRAIN_VALUES: constants.SALES_BASIS_GRAIN_VALUES,
  SALES_BASIS_SOURCE: constants.SALES_BASIS_SOURCE,
  SALES_BASIS_SOURCE_VALUES: constants.SALES_BASIS_SOURCE_VALUES,
  SALES_BASIS_FALLBACK_REASON: constants.SALES_BASIS_FALLBACK_REASON,
  SALES_BASIS_FALLBACK_REASON_VALUES: constants.SALES_BASIS_FALLBACK_REASON_VALUES,
  // PALM-PHONE-UAT-002 W3 (R-PU2-5): a store day's plan standing and the goal tile's fallback reasons — the
  // ONE vocabulary pos answers and pos-web / palm-web read (pos-api and pos-web each carried a copy before).
  DAY_PLAN_STATUS: constants.DAY_PLAN_STATUS,
  DAY_PLAN_STATUS_VALUES: constants.DAY_PLAN_STATUS_VALUES,
  DAILY_GOALS_FALLBACK_REASON: constants.DAILY_GOALS_FALLBACK_REASON,
  DAILY_GOALS_FALLBACK_REASON_VALUES: constants.DAILY_GOALS_FALLBACK_REASON_VALUES,
  // PLANNER-CANVAS-ONE-GRADE-001 U7c (R-OG-5): the ONE unit an hourly traffic profile is measured in.
  HOURLY_WEIGHTS_UNIT: constants.HOURLY_WEIGHTS_UNIT,
  // PLN-LY-TWO-GRAINS-001 P6 (R9): where a day's Monthly department set came from (planner → pos).
  MONTHLY_SET_SOURCE: constants.MONTHLY_SET_SOURCE,
  MONTHLY_SET_SOURCE_VALUES: constants.MONTHLY_SET_SOURCE_VALUES,
  // PLN-DEPT-GRAIN-001 (promoted by PLN-LY-TWO-GRAINS-001 P7): the planning grain a department is planned at.
  PLANNING_GRAIN: constants.PLANNING_GRAIN,
  PLANNING_GRAIN_VALUES: constants.PLANNING_GRAIN_VALUES,
  // PLN-LY-TWO-GRAINS-001 U4a (R15 / R35): the provenance a forecast-accuracy record is scored under.
  FORECAST_SCORING_EXCLUSIONS: constants.FORECAST_SCORING_EXCLUSIONS,
  FORECAST_SCORING_EXCLUSIONS_VALUES: constants.FORECAST_SCORING_EXCLUSIONS_VALUES,
  FORECAST_ACCURACY_SALES_BASIS: constants.FORECAST_ACCURACY_SALES_BASIS,
  FORECAST_ACCURACY_SALES_BASIS_VALUES: constants.FORECAST_ACCURACY_SALES_BASIS_VALUES,
  // PLANNER-PERFORMANCE-PAGE-001 U1 (D-3) — a rollup bucket's goal-attainment verdict, why its last-year
  // leg is withheld, and why its labor leg is withheld; planner-api's `constants/statuses` re-exports them
  // byte-equal, planner-web renders them, the parity registry pins both sides here.
  ATTAINMENT_STATUS: constants.ATTAINMENT_STATUS,
  ATTAINMENT_STATUS_VALUES: constants.ATTAINMENT_STATUS_VALUES,
  ROLLUP_LY_REASON: constants.ROLLUP_LY_REASON,
  ROLLUP_LY_REASON_VALUES: constants.ROLLUP_LY_REASON_VALUES,
  PERFORMANCE_LABOR_REASON: constants.PERFORMANCE_LABOR_REASON,
  PERFORMANCE_LABOR_REASON_VALUES: constants.PERFORMANCE_LABOR_REASON_VALUES,
  // PLN-LY-TWO-GRAINS-001 U4b (R16 / R12): why a report withholds its Monthly actual.
  MONTHLY_ACTUAL_NOTE: constants.MONTHLY_ACTUAL_NOTE,
  MONTHLY_ACTUAL_NOTE_VALUES: constants.MONTHLY_ACTUAL_NOTE_VALUES,
  // PLN-LY-TWO-GRAINS-001 U4b: the kind of a comparison anomaly — the web translates it.
  ANOMALY_TYPE: constants.ANOMALY_TYPE,
  ANOMALY_TYPE_VALUES: constants.ANOMALY_TYPE_VALUES,
  // PLN-LY-TWO-GRAINS-001 U5 (R2, Q-LT-11): WHY a plan day holds $0 by rule — a closed holiday or a
  // closed weekday — the `kind` on every closed-day disclosure and closed-date read, both tiers.
  CLOSED_DAY_KIND: constants.CLOSED_DAY_KIND,
  CLOSED_DAY_KIND_VALUES: constants.CLOSED_DAY_KIND_VALUES,

  // ── Day portion (palm_time_off_requests.dayPortion; LEAVE-CANON-001 P3d) ──
  AVAILABILITY_TYPE: constants.AVAILABILITY_TYPE,
  AVAILABILITY_TYPE_VALUES: constants.AVAILABILITY_TYPE_VALUES,
  TIME_OFF_REQUEST_STATUS: constants.TIME_OFF_REQUEST_STATUS,
  TIME_OFF_REQUEST_STATUS_VALUES: constants.TIME_OFF_REQUEST_STATUS_VALUES,
  TIME_OFF_WITHDRAWAL_STATUS: constants.TIME_OFF_WITHDRAWAL_STATUS,
  TIME_OFF_WITHDRAWAL_STATUS_VALUES: constants.TIME_OFF_WITHDRAWAL_STATUS_VALUES,
  OFFLINE_PUNCH_REPORT_STATUS: constants.OFFLINE_PUNCH_REPORT_STATUS,
  OFFLINE_PUNCH_REPORT_STATUS_VALUES: constants.OFFLINE_PUNCH_REPORT_STATUS_VALUES,
  PUNCH_CLOSURE_SOURCE: constants.PUNCH_CLOSURE_SOURCE,
  PUNCH_CLOSURE_SOURCE_VALUES: constants.PUNCH_CLOSURE_SOURCE_VALUES,
  OFFLINE_PUNCH_REPORT_KIND: constants.OFFLINE_PUNCH_REPORT_KIND,
  OFFLINE_PUNCH_REPORT_KIND_VALUES: constants.OFFLINE_PUNCH_REPORT_KIND_VALUES,
  DAY_PORTION: constants.DAY_PORTION,
  DAY_PORTION_VALUES: constants.DAY_PORTION_VALUES,
  DAY_PORTION_FRACTION: constants.DAY_PORTION_FRACTION,
  DEFAULT_DAY_PORTION: constants.DEFAULT_DAY_PORTION,
  dayPortionCoversWholeDay: constants.dayPortionCoversWholeDay,
  dayPortionRequiresWindow: constants.dayPortionRequiresWindow,
  // The request form's portion rules (LEAVE-CANON-001 9f / 9j-1) — palm-web and palm-native.
  isWindowInOrder: dayPortionWindow.isWindowInOrder,
  isPortionComplete: dayPortionWindow.isPortionComplete,
  portionPayload: dayPortionWindow.portionPayload,

  // ── Team invitation status (invitations.status; INVITATIONS-ONE-PATH D3d-2) ──
  INVITATION_STATUS: constants.INVITATION_STATUS,
  INVITATION_STATUS_VALUES: constants.INVITATION_STATUS_VALUES,
  OUTSTANDING_INVITATION_STATUSES: constants.OUTSTANDING_INVITATION_STATUSES,
  LEAVE_POLICY_STATUS: constants.LEAVE_POLICY_STATUS,
  LEAVE_POLICY_STATUS_VALUES: constants.LEAVE_POLICY_STATUS_VALUES,
  PAY_SCHEDULE_STATUS: constants.PAY_SCHEDULE_STATUS,
  PAY_SCHEDULE_STATUS_VALUES: constants.PAY_SCHEDULE_STATUS_VALUES,

  // ── A PortPuffin mailbox holder's subscription status and plan tier (CUSTOMER-ERASURE-REACH step 2) ──
  MAILBOX_HOLDER_SUBSCRIPTION_STATUS: constants.MAILBOX_HOLDER_SUBSCRIPTION_STATUS,
  MAILBOX_HOLDER_SUBSCRIPTION_STATUS_VALUES: constants.MAILBOX_HOLDER_SUBSCRIPTION_STATUS_VALUES,
  MAILBOX_HOLDER_PLAN_TIER: constants.MAILBOX_HOLDER_PLAN_TIER,
  MAILBOX_HOLDER_PLAN_TIER_VALUES: constants.MAILBOX_HOLDER_PLAN_TIER_VALUES,

  // ── Tenant platform subscription status (companies.subscriptionStatus; USER-FACING-GEMS L5) ──
  SUBSCRIPTION_STATUS: constants.SUBSCRIPTION_STATUS,
  SUBSCRIPTION_STATUS_VALUES: constants.SUBSCRIPTION_STATUS_VALUES,
  BILLING_ATTENTION_SUBSCRIPTION_STATUSES: constants.BILLING_ATTENTION_SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_STATUS_BADGE_TONES: constants.SUBSCRIPTION_STATUS_BADGE_TONES,
  // How the plan is paid (subscriptions.billingMode; TENANT-PUBLIC-FK-001 complimentary plans).
  SUBSCRIPTION_BILLING_MODE: constants.SUBSCRIPTION_BILLING_MODE,
  SUBSCRIPTION_BILLING_MODE_VALUES: constants.SUBSCRIPTION_BILLING_MODE_VALUES,
  TRIAL_PERIOD_DAYS: constants.TRIAL_PERIOD_DAYS,

  // ── The named warnings a time-off submission carries (LEAVE-CANON-001 P3a / 9j-2) ──
  LEAVE_WARNING_CODES: constants.LEAVE_WARNING_CODES,

  // ── Leave blackout periods (planner_leave_blackouts; LEAVE-CANON-001 P3a / 9j-1) ──
  LEAVE_BLACKOUT_ENFORCEMENTS: leaveBlackouts.LEAVE_BLACKOUT_ENFORCEMENTS,
  classifyBlackoutOverlap: leaveBlackouts.classifyBlackoutOverlap,
  judgeBlackoutOverlap: leaveBlackouts.judgeBlackoutOverlap,
  // When two portions of one date collide — two leave requests (palm), or a leave request and a
  // published shift (planner, LEAVE-CANON-001 Phase 3). One rule, so they never disagree.
  dayPortionsOverlap: dayPortionOverlap.dayPortionsOverlap,
  clockMinutes: dayPortionOverlap.clockMinutes,

  // ── Schedule absence (call-out) reason (planner_schedule_absences; PLN-SCHED-LIFECYCLE-001) ──
  SCHEDULE_ABSENCE_REASON: constants.SCHEDULE_ABSENCE_REASON,
  SCHEDULE_ABSENCE_REASON_VALUES: constants.SCHEDULE_ABSENCE_REASON_VALUES,
  SELF_REPORTED_ABSENCE_REASONS: constants.SELF_REPORTED_ABSENCE_REASONS,

  // ── Shipment lifecycle (shipments.status; LEAVE-CANON-001 R14 F-R14-7c, owner ruling R-71) ──
  SHIPMENT_STATUS: shipmentStatus.SHIPMENT_STATUS,
  SHIPMENT_STATUS_VALUES: shipmentStatus.SHIPMENT_STATUS_VALUES,
  SHIPMENT_STATUS_TRANSITIONS: shipmentStatus.SHIPMENT_STATUS_TRANSITIONS,
  SHIPMENT_STATUS_BADGE_TONES: shipmentStatus.SHIPMENT_STATUS_BADGE_TONES,
  SHIPMENT_STATUS_ASSIGNABLE_VALUES: shipmentStatus.SHIPMENT_STATUS_ASSIGNABLE_VALUES,
  shipmentStatusTargets: shipmentStatus.shipmentStatusTargets,
  // DTOWNRETAIL-SITE-001 V1-b — a CARRIER's scan vocabulary (EasyPost's tracker): purchase-api reads it from the
  // carrier and the storefront block words it, so it lives in neither.
  CARRIER_SCAN_STATUS: shipmentStatus.CARRIER_SCAN_STATUS,
  CARRIER_SCAN_STATUS_VALUES: shipmentStatus.CARRIER_SCAN_STATUS_VALUES,
  SHIPMENT_STATUS_OF_CARRIER_SCAN: shipmentStatus.SHIPMENT_STATUS_OF_CARRIER_SCAN,
  shipmentStatusOfCarrierScan: shipmentStatus.shipmentStatusOfCarrierScan,

  // ── Mail-item lifecycle — the customer-pickup axis (mail_items.status; LEAVE-CANON-001 R-74) ──
  MAIL_ITEM_TYPE: mailItemStatus.MAIL_ITEM_TYPE,
  MAIL_ITEM_TYPE_VALUES: mailItemStatus.MAIL_ITEM_TYPE_VALUES,
  MAIL_ITEM_STATUS: mailItemStatus.MAIL_ITEM_STATUS,
  MAIL_ITEM_STATUS_VALUES: mailItemStatus.MAIL_ITEM_STATUS_VALUES,
  MAIL_ITEM_STATUS_TRANSITIONS: mailItemStatus.MAIL_ITEM_STATUS_TRANSITIONS,
  MAIL_ITEM_STATUS_BADGE_TONES: mailItemStatus.MAIL_ITEM_STATUS_BADGE_TONES,
  mailItemStatusTargets: mailItemStatus.mailItemStatusTargets,

  // ── Mailbox rentals — the box, the agreement that holds it, types and billing terms (L-R7CLOSE row 54, Q-R8CLOSE-39) ──
  MAILBOX_STATUS: mailboxRental.MAILBOX_STATUS,
  MAILBOX_STATUS_VALUES: mailboxRental.MAILBOX_STATUS_VALUES,
  MAILBOX_STATUS_TRANSITIONS: mailboxRental.MAILBOX_STATUS_TRANSITIONS,
  MAILBOX_STATUS_BADGE_TONES: mailboxRental.MAILBOX_STATUS_BADGE_TONES,
  MAILBOX_HELD_STATUSES: mailboxRental.MAILBOX_HELD_STATUSES,
  isMailboxHeld: mailboxRental.isMailboxHeld,
  RENTAL_OWNED_EDGES: mailboxRental.RENTAL_OWNED_EDGES,
  isRentalOwnedEdge: mailboxRental.isRentalOwnedEdge,
  mailboxStatusTargets: mailboxRental.mailboxStatusTargets,
  MAILBOX_RENTAL_STATUS: mailboxRental.MAILBOX_RENTAL_STATUS,
  MAILBOX_RENTAL_STATUS_VALUES: mailboxRental.MAILBOX_RENTAL_STATUS_VALUES,
  MAILBOX_RENTAL_STATUS_TRANSITIONS: mailboxRental.MAILBOX_RENTAL_STATUS_TRANSITIONS,
  MAILBOX_RENTAL_STATUS_BADGE_TONES: mailboxRental.MAILBOX_RENTAL_STATUS_BADGE_TONES,
  MAILBOX_RENTAL_END_REASON: mailboxRental.MAILBOX_RENTAL_END_REASON,
  MAILBOX_RENTAL_END_REASON_VALUES: mailboxRental.MAILBOX_RENTAL_END_REASON_VALUES,
  MAILBOX_TYPE: mailboxRental.MAILBOX_TYPE,
  MAILBOX_TYPE_VALUES: mailboxRental.MAILBOX_TYPE_VALUES,
  RENTAL_BILLING_PERIOD: mailboxRental.RENTAL_BILLING_PERIOD,
  RENTAL_BILLING_PERIOD_VALUES: mailboxRental.RENTAL_BILLING_PERIOD_VALUES,
  RENTAL_TERM_MONTHS: mailboxRental.RENTAL_TERM_MONTHS,

  // ── Mailbox rent payments — a payment attempt and an autopay authorization (MAILBOX-RENT-PAYMENTS-001; R-SSC-3, R-MRP-1…4) ──
  MAILBOX_RENT_PAYMENT_STATUS: mailboxRentPayment.MAILBOX_RENT_PAYMENT_STATUS,
  MAILBOX_RENT_PAYMENT_STATUS_VALUES: mailboxRentPayment.MAILBOX_RENT_PAYMENT_STATUS_VALUES,
  MAILBOX_RENT_PAYMENT_STATUS_TRANSITIONS:
    mailboxRentPayment.MAILBOX_RENT_PAYMENT_STATUS_TRANSITIONS,
  MAILBOX_RENT_PAYMENT_STATUS_BADGE_TONES:
    mailboxRentPayment.MAILBOX_RENT_PAYMENT_STATUS_BADGE_TONES,
  MAILBOX_RENT_PAYMENT_SETTLING_STATUSES: mailboxRentPayment.MAILBOX_RENT_PAYMENT_SETTLING_STATUSES,
  mailboxRentPaymentStatusTargets: mailboxRentPayment.mailboxRentPaymentStatusTargets,
  AUTOPAY_AUTHORIZATION_STATUS: mailboxRentPayment.AUTOPAY_AUTHORIZATION_STATUS,
  AUTOPAY_AUTHORIZATION_STATUS_VALUES: mailboxRentPayment.AUTOPAY_AUTHORIZATION_STATUS_VALUES,
  AUTOPAY_AUTHORIZATION_STATUS_TRANSITIONS:
    mailboxRentPayment.AUTOPAY_AUTHORIZATION_STATUS_TRANSITIONS,
  AUTOPAY_AUTHORIZATION_STATUS_BADGE_TONES:
    mailboxRentPayment.AUTOPAY_AUTHORIZATION_STATUS_BADGE_TONES,
  autopayAuthorizationStatusTargets: mailboxRentPayment.autopayAuthorizationStatusTargets,

  // ── Mailbox rental agreements — an agreement, its terms and its signature (MAILBOX-SIGNUP-JOURNEY-001 B3; O6, O7) ──
  MAILBOX_AGREEMENT_STATUS: mailboxAgreement.MAILBOX_AGREEMENT_STATUS,
  MAILBOX_AGREEMENT_STATUS_VALUES: mailboxAgreement.MAILBOX_AGREEMENT_STATUS_VALUES,
  MAILBOX_AGREEMENT_STATUS_TRANSITIONS: mailboxAgreement.MAILBOX_AGREEMENT_STATUS_TRANSITIONS,
  MAILBOX_AGREEMENT_STATUS_BADGE_TONES: mailboxAgreement.MAILBOX_AGREEMENT_STATUS_BADGE_TONES,
  mailboxAgreementStatusTargets: mailboxAgreement.mailboxAgreementStatusTargets,
  MAILBOX_AGREEMENT_TEMPLATE_SOURCE: mailboxAgreement.MAILBOX_AGREEMENT_TEMPLATE_SOURCE,
  MAILBOX_AGREEMENT_TEMPLATE_SOURCE_VALUES:
    mailboxAgreement.MAILBOX_AGREEMENT_TEMPLATE_SOURCE_VALUES,
  MAILBOX_AGREEMENT_LANGUAGE: mailboxAgreement.MAILBOX_AGREEMENT_LANGUAGE,
  MAILBOX_AGREEMENT_LANGUAGE_VALUES: mailboxAgreement.MAILBOX_AGREEMENT_LANGUAGE_VALUES,
  MAILBOX_AGREEMENT_SIGNATURE_METHOD: mailboxAgreement.MAILBOX_AGREEMENT_SIGNATURE_METHOD,
  MAILBOX_AGREEMENT_SIGNATURE_METHOD_VALUES:
    mailboxAgreement.MAILBOX_AGREEMENT_SIGNATURE_METHOD_VALUES,
  MAILBOX_AGREEMENT_COPY_CHANNEL: mailboxAgreement.MAILBOX_AGREEMENT_COPY_CHANNEL,
  MAILBOX_AGREEMENT_COPY_CHANNEL_VALUES: mailboxAgreement.MAILBOX_AGREEMENT_COPY_CHANNEL_VALUES,

  // ── Customer record lifecycle — who may sign in (customers.status; RLT-Q40 "Only active may sign in") ──
  CUSTOMER_STATUS: customerStatus.CUSTOMER_STATUS,
  CUSTOMER_STATUS_VALUES: customerStatus.CUSTOMER_STATUS_VALUES,
  CUSTOMER_STATUS_ASSIGNABLE_VALUES: customerStatus.CUSTOMER_STATUS_ASSIGNABLE_VALUES,
  isCustomerSignInAllowed: customerStatus.isCustomerSignInAllowed,
  // ── Print-job lifecycle (print_jobs.status; LEAVE-CANON-001 R15, owner ruling on Q-R14-24) ──
  PRINT_JOB_STATUS: printJobStatus.PRINT_JOB_STATUS,
  PRINT_JOB_STATUS_VALUES: printJobStatus.PRINT_JOB_STATUS_VALUES,
  PRINT_JOB_STATUS_TRANSITIONS: printJobStatus.PRINT_JOB_STATUS_TRANSITIONS,
  PRINT_JOB_STATUS_BADGE_TONES: printJobStatus.PRINT_JOB_STATUS_BADGE_TONES,
  printJobStatusTargets: printJobStatus.printJobStatusTargets,
  // ── Operation-run lifecycle (a long platform operation the console follows; TENANT-CREATION-JOB-001, Q-TCJ-3/4) ──
  OPERATION_RUN_STATUS: operationRunStatus.OPERATION_RUN_STATUS,
  OPERATION_RUN_STATUS_VALUES: operationRunStatus.OPERATION_RUN_STATUS_VALUES,
  OPERATION_RUN_ACTIVE_STATUSES: operationRunStatus.OPERATION_RUN_ACTIVE_STATUSES,
  OPERATION_RUN_STATUS_TRANSITIONS: operationRunStatus.OPERATION_RUN_STATUS_TRANSITIONS,
  OPERATION_RUN_STATUS_BADGE_TONES: operationRunStatus.OPERATION_RUN_STATUS_BADGE_TONES,
  operationRunStatusTargets: operationRunStatus.operationRunStatusTargets,
  isOperationRunActive: operationRunStatus.isOperationRunActive,
  // POS-PRINT-LAYOUTS-001 M1.9: the store print queue's job lifecycle (print exactly once; a reprint is a NEW job).
  PRINT_QUEUE_JOB_STATUS: printQueueJobStatus.PRINT_QUEUE_JOB_STATUS,
  PRINT_QUEUE_JOB_STATUS_VALUES: printQueueJobStatus.PRINT_QUEUE_JOB_STATUS_VALUES,
  PRINT_QUEUE_JOB_ACTIVE_STATUSES: printQueueJobStatus.PRINT_QUEUE_JOB_ACTIVE_STATUSES,
  PRINT_QUEUE_JOB_STATUS_TRANSITIONS: printQueueJobStatus.PRINT_QUEUE_JOB_STATUS_TRANSITIONS,
  PRINT_QUEUE_JOB_STATUS_BADGE_TONES: printQueueJobStatus.PRINT_QUEUE_JOB_STATUS_BADGE_TONES,
  printQueueJobStatusTargets: printQueueJobStatus.printQueueJobStatusTargets,
  isPrintQueueJobActive: printQueueJobStatus.isPrintQueueJobActive,
  // ── Order source (`orders.orderSourceType`; REGISTER-WEB-ORDERS-001 A3, owner rulings Q-RWO-0..4) ──
  ORDER_SOURCE: orderSource.ORDER_SOURCE,
  ORDER_SOURCE_VALUES: orderSource.ORDER_SOURCE_VALUES,
  PURCHASE_ORDER_SOURCES: orderSource.PURCHASE_ORDER_SOURCES,
  // ── The register's transaction ledger and tenders (`transactions`, `payments`; REGISTER-WEB-ORDERS-001 B7b, moved
  //    out of pos-api with every reader — GAMING 1341Z / 1352Z) ──
  TRANSACTION_REFERENCE_TYPE: registerTransaction.TRANSACTION_REFERENCE_TYPE,
  TRANSACTION_TYPE: registerTransaction.TRANSACTION_TYPE,
  TRANSACTION_STATUS: registerTransaction.TRANSACTION_STATUS,
  VOID_TRANSACTION_STATUS: registerTransaction.VOID_TRANSACTION_STATUS,
  PERSISTED_TRANSACTION_STATUSES: registerTransaction.PERSISTED_TRANSACTION_STATUSES,
  REGISTER_PAYMENT_STATUS: registerPayment.REGISTER_PAYMENT_STATUS,
  REGISTER_PAYMENT_STATUSES: registerPayment.REGISTER_PAYMENT_STATUSES,
  // ── Inventory movement type (`inventory_transactions.transactionType`; moved from @puffin-core/inventory, SEAM-001 F9) ──
  INVENTORY_MOVEMENT_TYPES: inventoryMovementType.INVENTORY_MOVEMENT_TYPES,
  // ── Order line type (`order_line_items.type`; REGISTER-WEB-ORDERS-001 A4, owner ruling Q-RWO-1) ──
  ORDER_LINE_TYPE: orderLineType.ORDER_LINE_TYPE,
  ORDER_LINE_TYPE_VALUES: orderLineType.ORDER_LINE_TYPE_VALUES,
  UNDISCOUNTED_ORDER_LINE_TYPES: orderLineType.UNDISCOUNTED_ORDER_LINE_TYPES,
  // ── Order statuses (`orders.status` / `paymentStatus` / `fulfillmentStatus`; REGISTER-WEB-ORDERS-001 B1, ORD1) ──
  ORDER_STATUS: orderStatus.ORDER_STATUS,
  ORDER_STATUS_VALUES: orderStatus.ORDER_STATUS_VALUES,
  ORDER_STATUS_TRANSITIONS: orderStatus.ORDER_STATUS_TRANSITIONS,
  ORDER_OPEN_STATUSES: orderStatus.ORDER_OPEN_STATUSES,
  ORDER_FINAL_STATUSES: orderStatus.ORDER_FINAL_STATUSES,
  ORDER_CANCELLABLE_STATUSES: orderStatus.ORDER_CANCELLABLE_STATUSES,
  ORDER_STATUS_BADGE_TONES: orderStatus.ORDER_STATUS_BADGE_TONES,
  orderStatusTargets: orderStatus.orderStatusTargets,
  ORDER_PAYMENT_STATUS: orderStatus.ORDER_PAYMENT_STATUS,
  ORDER_PAYMENT_STATUS_VALUES: orderStatus.ORDER_PAYMENT_STATUS_VALUES,
  ORDER_PAYMENT_CLASS: orderStatus.ORDER_PAYMENT_CLASS,
  ORDER_PAYMENT_STATUS_CLASSES: orderStatus.ORDER_PAYMENT_STATUS_CLASSES,
  ORDER_REFUND_PAYMENT_STATUSES: orderStatus.ORDER_REFUND_PAYMENT_STATUSES,
  ORDER_PAYMENT_FULLY_SETTLED_STATUSES: orderStatus.ORDER_PAYMENT_FULLY_SETTLED_STATUSES,
  ORDER_AWAITING_PAYMENT_STATUSES: orderStatus.ORDER_AWAITING_PAYMENT_STATUSES,
  ORDER_PAYMENT_STATUS_BADGE_TONES: orderStatus.ORDER_PAYMENT_STATUS_BADGE_TONES,
  orderPaymentStatusClass: orderStatus.orderPaymentStatusClass,
  isOrderPaymentFullySettled: orderStatus.isOrderPaymentFullySettled,
  ORDER_FULFILLMENT_STATUS: orderStatus.ORDER_FULFILLMENT_STATUS,
  ORDER_FULFILLMENT_STATUS_VALUES: orderStatus.ORDER_FULFILLMENT_STATUS_VALUES,
  ORDER_UNSTARTED_FULFILLMENT_STATUS: orderStatus.ORDER_UNSTARTED_FULFILLMENT_STATUS,
  ORDER_PICKUP_FULFILLMENT: orderStatus.ORDER_PICKUP_FULFILLMENT,
  ORDER_PICKUP_READY_FROM_FULFILLMENT_STATUSES:
    orderStatus.ORDER_PICKUP_READY_FROM_FULFILLMENT_STATUSES,
  ORDER_CARRIER_FULFILLMENT_STATUSES: orderStatus.ORDER_CARRIER_FULFILLMENT_STATUSES,
  ORDER_UNCANCELLABLE_FULFILLMENT_STATUSES: orderStatus.ORDER_UNCANCELLABLE_FULFILLMENT_STATUSES,
  ORDER_GOODS_LEFT_FULFILLMENT_STATUSES: orderStatus.ORDER_GOODS_LEFT_FULFILLMENT_STATUSES,
  ORDER_FULFILLMENT_STATUS_BADGE_TONES: orderStatus.ORDER_FULFILLMENT_STATUS_BADGE_TONES,
  // ── Text-to-speech providers (REGISTER-WEB-ORDERS-001, the TTS ONE vocabulary) ──
  TTS_PROVIDER: ttsProvider.TTS_PROVIDER,
  TTS_PROVIDER_VALUES: ttsProvider.TTS_PROVIDER_VALUES,
  LIVE_TTS_PROVIDERS: ttsProvider.LIVE_TTS_PROVIDERS,
  DEFAULT_TTS_PROVIDER: ttsProvider.DEFAULT_TTS_PROVIDER,
  VOICE_CLONING_PROVIDER: ttsProvider.VOICE_CLONING_PROVIDER,

  // ── Display SSOT ──
  ACCOUNT_STATUS_LABELS: constants.ACCOUNT_STATUS_LABELS,
  EMPLOYMENT_STATUS_LABELS: constants.EMPLOYMENT_STATUS_LABELS,
  ACCOUNT_STATUS_BADGE_TONES: constants.ACCOUNT_STATUS_BADGE_TONES,
  EMPLOYMENT_STATUS_BADGE_TONES: constants.EMPLOYMENT_STATUS_BADGE_TONES,

  // ── Tenant status (`public.companies.status`) + its one tone map (Q-MI-25 F-DIVERGE-TENANT) ──
  TENANT_STATUS: constants.TENANT_STATUS,
  TENANT_STATUS_VALUES: constants.TENANT_STATUS_VALUES,
  LIVE_TENANT_STATUSES: constants.LIVE_TENANT_STATUSES,
  TENANT_STATUS_BADGE_TONES: constants.TENANT_STATUS_BADGE_TONES,

  // ── Badge tones (Q-MI-25): the closed `.badge[data-tone]` vocabulary + its one resolver ──
  BADGE_TONE: badgeTones.BADGE_TONE,
  BADGE_TONE_VALUES: badgeTones.BADGE_TONE_VALUES,
  isBadgeTone: badgeTones.isBadgeTone,
  resolveBadgeTone: badgeTones.resolveBadgeTone,
  SCHEDULE_BLOCKER: constants.SCHEDULE_BLOCKER,
  SCHEDULE_BLOCKER_LABELS: constants.SCHEDULE_BLOCKER_LABELS,

  // ── Derived helpers ──
  deriveAccessState: helpers.deriveAccessState,
  deriveScheduleReadiness: helpers.deriveScheduleReadiness,
  isSchedulable: helpers.isSchedulable,
  isOnRoster: helpers.isOnRoster,

  // ── Option builders (filter/select dropdowns) ──
  buildStatusOptions: helpers.buildStatusOptions,
  buildEmploymentStatusOptions: helpers.buildEmploymentStatusOptions,
};
